import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle } from '../theme';

// J5 — Owner's single-page KPI view. Pulls data from multiple existing
// endpoints in parallel and lays out critical numbers in one scroll.

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const todayStr = () => new Date().toISOString().split('T')[0];
const daysAgoStr = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };

const ExecutiveDashboardPage = ({ API_BASE }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from30 = daysAgoStr(30);
      const to    = todayStr();

      const safe = (p) => p.then(r => r.data).catch(() => null);

      const [stats, docRev, ltv, cashFlow, referrers, outcomes, risks, pendingIncentives] = await Promise.all([
        safe(axios.get(`${API_BASE}/dashboard/stats`)),
        safe(axios.get(`${API_BASE}/analytics-engine/doctor-revenue`, { params: { from: from30, to } })),
        safe(axios.get(`${API_BASE}/analytics-engine/patient-ltv`, { params: { limit: 10 } })),
        safe(axios.get(`${API_BASE}/analytics-engine/cash-flow-forecast`, { params: { days: 30 } })),
        safe(axios.get(`${API_BASE}/patient-referrals/leaderboard`, { params: { limit: 5 } })),
        safe(axios.get(`${API_BASE}/outcomes/stats`)),
        safe(axios.get(`${API_BASE}/risk-scores/top/churnRisk`, { params: { limit: 10 } })),
        safe(axios.get(`${API_BASE}/incentives`, { params: { status: 'pending' } })),
      ]);

      setData({ stats, docRev, ltv, cashFlow, referrers, outcomes, risks, pendingIncentives });
      setLastRefreshed(new Date());
    } catch (e) { /* per-call failures already swallowed */ }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const { stats, docRev, ltv, cashFlow, referrers, outcomes, risks, pendingIncentives } = data;

  // Derived KPIs
  const totalOutcomes = (outcomes || []).reduce((s, o) => s + o.total, 0);
  const successCount  = (outcomes || []).reduce((s, o) => s + o.breakdown.success, 0);
  const avgSuccess    = totalOutcomes ? Math.round((successCount / totalOutcomes) * 100) : null;

  const pendingIncentiveValue = (pendingIncentives || []).reduce((s, i) => s + (i.amount || 0), 0);
  const highRiskPatients = (risks || []).filter(r => r.churnRisk >= 75).length;
  const expectedCashFlow = cashFlow?.totalExpected || cashFlow?.total || 0;

  const kpiCard = (label, value, sub, color) => (
    <div style={{ ...cardStyle, padding:'16px 18px', borderLeft:`4px solid ${color || theme.primary}` }}>
      <div style={{ fontSize:'0.7rem', color:theme.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
      <div style={{ fontSize:'1.8rem', fontWeight:800, color: color || theme.navy, marginTop:6 }}>{value}</div>
      {sub && <div style={{ fontSize:'0.72rem', color:'#888', marginTop:4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>📊 Executive Dashboard</h2>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {lastRefreshed && (
            <span style={{ fontSize:'0.75rem', color:'#888' }}>
              Updated {lastRefreshed.toLocaleTimeString('en-IN')}
            </span>
          )}
          <button onClick={load} disabled={loading}
            style={{ padding:'6px 14px', background:theme.primary, color:'white', border:'none', borderRadius:6, fontWeight:600, fontSize:'0.82rem', cursor:'pointer' }}>
            {loading ? 'Loading…' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Hero KPI row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:20 }}>
        {kpiCard("Today's Revenue", fmt(stats?.todaysRevenue), `${stats?.todaysPatients || 0} new patients`, theme.success)}
        {kpiCard('Total Revenue (all time)', fmt(stats?.totalRevenue), `${stats?.totalPatients || 0} total patients`, theme.navy)}
        {kpiCard('Expected Cash Flow (30d)', fmt(expectedCashFlow), 'From scheduled + pending', '#0891b2')}
        {kpiCard('Pending Incentives', fmt(pendingIncentiveValue), `${(pendingIncentives || []).length} items to approve`, theme.warning)}
        {kpiCard('Avg Treatment Success', avgSuccess != null ? `${avgSuccess}%` : '—', `${totalOutcomes} outcomes recorded`, avgSuccess >= 85 ? theme.success : avgSuccess >= 70 ? '#0891b2' : theme.danger)}
        {kpiCard('High-Churn-Risk Patients', highRiskPatients, 'Score ≥ 75', highRiskPatients > 5 ? theme.danger : theme.warning)}
      </div>

      {/* Row: Top Doctors + LTV Segments */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={headerBar}>👨‍⚕️ Top Doctors (30d)</div>
          {(() => {
            // API returns { rows:[...], totals:{...} } — use rows. Guard against legacy array shape too.
            const rows = Array.isArray(docRev) ? docRev : (docRev?.rows || []);
            if (rows.length === 0) return <div style={emptyBox}>No data yet</div>;
            return (
              <div>
                {rows.slice(0, 6).map((d, i) => (
                  <div key={i} style={rowStyle}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{d.name || d.doctor || d._id || '—'}</div>
                      <div style={{ fontSize:'0.72rem', color:'#666' }}>
                        {d.patientCount || d.patients || 0} patients · {d.treatmentCount || d.treatments || d.lineCount || 0} treatments
                      </div>
                    </div>
                    <div style={{ fontWeight:700, color:theme.success }}>{fmt(d.revenue || d.totalRevenue)}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={headerBar}>💎 Patient Value Segments</div>
          {!ltv || !ltv.segments
            ? <div style={emptyBox}>No data yet</div>
            : (
              <div>
                <div style={{ padding:'14px 16px', borderBottom:'1px solid #f1f3f5' }}>
                  <div style={{ fontSize:'0.72rem', color:'#888', textTransform:'uppercase', fontWeight:600 }}>Total Lifetime Value</div>
                  <div style={{ fontSize:'1.5rem', fontWeight:800, color:theme.success }}>{fmt(ltv.totals?.totalValue)}</div>
                  <div style={{ fontSize:'0.72rem', color:'#666' }}>Avg {fmt(ltv.totals?.avgLtv)} across {ltv.totals?.patients || 0} patients</div>
                </div>
                <div style={{ padding:'10px 16px' }}>
                  {Object.entries(ltv.segments).map(([k, v]) => {
                    const label = { vip:'💎 VIP', loyal:'⭐ Loyal', regular:'👥 Regular', new:'🆕 New', dormant:'💤 Dormant' }[k] || k;
                    return (
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:'0.85rem' }}>
                        <span>{label}</span>
                        <span style={{ fontWeight:700 }}>{v}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Row: Top Referrers + Top LTV Patients */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={headerBar}>🏆 Top Referrers</div>
          {(referrers || []).length === 0
            ? <div style={emptyBox}>No referrals yet</div>
            : (referrers || []).map((r, i) => (
              <div key={r._id} style={rowStyle}>
                <div>
                  <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{i < 3 ? ['🥇','🥈','🥉'][i] : `#${i+1}`} {r.referrerName}</div>
                  <div style={{ fontSize:'0.72rem', color:'#666' }}>{r.rewarded} rewarded · {r.pending} pending</div>
                </div>
                <div style={{ fontWeight:700, color:theme.success }}>{fmt(r.rewardValue)}</div>
              </div>
            ))}
        </div>

        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={headerBar}>💰 Top Spenders</div>
          {(ltv?.top || []).length === 0
            ? <div style={emptyBox}>No data yet</div>
            : ltv.top.slice(0, 6).map((p, i) => (
              <div key={p.patientId} style={rowStyle}>
                <div>
                  <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{p.name}</div>
                  <div style={{ fontSize:'0.72rem', color:'#666' }}>{p.visits} visits · last {p.recencyDays}d ago</div>
                </div>
                <div style={{ fontWeight:700, color:theme.success }}>{fmt(p.totalSpend)}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Row: Risks + Outcomes */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={{ ...headerBar, background:'#fef3c7' }}>⚠️ At-Risk Patients (Churn)</div>
          {(risks || []).length === 0
            ? <div style={emptyBox}>No at-risk patients</div>
            : risks.slice(0, 6).map(r => {
                const level = r.churnRisk >= 75 ? { bg:'#fee2e2', fg:'#991b1b' } : r.churnRisk >= 50 ? { bg:'#fef3c7', fg:'#92400e' } : { bg:'#e0f2fe', fg:'#075985' };
                return (
                  <div key={r._id} style={rowStyle}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{r.patientName}</div>
                      <div style={{ fontSize:'0.72rem', color:'#666' }}>
                        {r.features?.lastVisitDaysAgo === 9999 ? 'Never returned' : `Last ${r.features?.lastVisitDaysAgo || 0}d ago`}
                      </div>
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background:level.bg, color:level.fg }}>
                      {r.churnRisk}/100
                    </span>
                  </div>
                );
              })}
        </div>

        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={{ ...headerBar, background:'#dcfce7' }}>🏆 Treatment Success Rates</div>
          {(outcomes || []).length === 0
            ? <div style={emptyBox}>No outcomes recorded yet</div>
            : outcomes.slice(0, 6).map((o, i) => {
                const c = o.successRate >= 85 ? { bg:'#dcfce7', fg:'#166534' } : o.successRate >= 70 ? { bg:'#e0f2fe', fg:'#075985' } : { bg:'#fee2e2', fg:'#991b1b' };
                return (
                  <div key={i} style={rowStyle}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{o.treatmentName}</div>
                      <div style={{ fontSize:'0.72rem', color:'#666' }}>{o.total} cases{o.avgSatisfaction ? ` · ⭐ ${o.avgSatisfaction}` : ''}</div>
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background:c.bg, color:c.fg }}>
                      {o.successRate}%
                    </span>
                  </div>
                );
              })}
        </div>
      </div>

      <div style={{ ...cardStyle, background:theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.82rem', color:theme.navy, lineHeight:1.5 }}>
          <b>One-page clinic health:</b> revenue, doctor performance, patient value, referral growth, churn risk, treatment outcomes. Click "🔄 Refresh" for live numbers. Deep-dive into any card by navigating to the module directly.
        </div>
      </div>
    </div>
  );
};

const headerBar = { padding:'10px 14px', background:'#f9fafb', fontWeight:700, fontSize:'0.88rem', color:theme.navy, borderBottom:'1px solid #e5e7eb' };
const rowStyle = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:'1px solid #f1f3f5' };
const emptyBox = { padding:30, textAlign:'center', color:'#888', fontSize:'0.85rem' };

export default ExecutiveDashboardPage;
